import { Auth0Provider } from '@auth0/auth0-react';
import { datadogRum } from '@datadog/browser-rum';
import { LDEvaluationDetail, LDProvider, useFlags } from '@mikecousins/launchdarkly-react-client-sdk';
import { Outlet } from '@remix-run/react';
import { SparkProvider } from '@spark-web/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { ClientOnly } from 'remix-utils/client-only';

import { Document } from './_document';
import { env } from './env/client';

import 'rsuite/dist/rsuite.min.css';

import { Maintenance } from './components/maintenance';

export function Layout({ children }: { children: React.ReactNode }) {
  return <Document>{children}</Document>;
}

function AppContent() {
  const [queryClient] = useState(() => new QueryClient());

  const flags = useFlags();

  const maintenancePageFlag = flags.maintenancePageEnterpriseDashboard;

  // Show maintenance page if maintenance mode is enabled
  if (maintenancePageFlag?.isEnabled) {
    return <Maintenance endDate={maintenancePageFlag.endDate} />;
  }

  return (
    <Auth0Provider
      domain={env.PUBLIC_AUTH0_DOMAIN}
      clientId={env.PUBLIC_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: env.PUBLIC_AUTH0_AUDIENCE,
        scope:
          'openid profile email read:enterprise-leads read:vendors read:enterprise-contacts read:enterprise-documents create:enterprise-document enterprise.partner.application:read',
      }}
      useCookiesForTransactions
    >
      <QueryClientProvider client={queryClient}>
        <SparkProvider>
          <Outlet />
        </SparkProvider>
      </QueryClientProvider>
    </Auth0Provider>
  );
}

export default function App() {
  return (
    <LDProvider
      clientSideID={env.PUBLIC_LAUNCH_DARKLY_CLIENT_ID}
      options={{
        // The SDK's own anonymous usage-stats beacon has no effect on flag evaluation;
        // disabling it removes a recurring, unactionable network-error log source.
        diagnosticOptOut: true,
        inspectors: [
          {
            type: 'flag-used',
            name: 'dd-inspector',
            method: (key: string, detail: LDEvaluationDetail) => {
              datadogRum.addFeatureFlagEvaluation(key, detail.value);
            },
          },
        ],
      }}
    >
      <ClientOnly>{() => <AppContent />}</ClientOnly>
    </LDProvider>
  );
}
